// src/utils/mesFnct.ts — SCE Issues
// « Mes FNC-T » (lot 4 du § 14, 22/10 ; maquette https://claude.ai/artifact/2nfLQyNb8UjEEBZs5VCmNm) :
// mes actions, puis les FNC-T qui me concernent — celles que j'ai ouvertes, que
// je mène, que je suis — avec leur état. Pur, testable hors ligne.
//
// « Moi » : l'adresse quand la ligne en porte une (ResponsableUPN, LeaderUPN),
// sinon le nom (`memePersonne` : casse, accents, ordre). « Voir pour » (revue,
// administrateur) cherche une autre personne par son nom seul.

import type { ActionFnct, Fnct, Jour } from '@/types/fnct.types'
import { cleNom, debutFnct, echeanceEnVigueur, estEnReleve, estTerminee, joursEntre, memePersonne, statutAction } from './fnct'
import { decalerJours, rangDansSemaine } from './calendrier'
import { pretAClore } from './ficheFnct'
import { suitFnct } from './fnctSuivies'
import { beAttendu } from './attenteBE'
import { nombreDeReports } from './actionsFnct'

export interface Qui {
  /** « Prénom Nom ». */
  nom: string
  /** Vide pour « Voir pour » : la personne n'est cherchée que par son nom. */
  upn: string
}

export function estMoi(nom: string, upn: string | undefined, qui: Qui): boolean {
  if (upn && qui.upn && upn.trim().toLowerCase() === qui.upn.trim().toLowerCase()) return true
  return nom.trim() !== '' && memePersonne(nom, qui.nom)
}

// ─── Mes actions ───

export interface MonAction {
  action: ActionFnct
  fnct: Fnct
  /** L'échéance qui compte aujourd'hui (la reportée si elle existe). */
  echeance: Jour | null
  /** Jours de retard ; 0 si l'action n'est pas en retard. */
  retard: number
  /** Combien de fois elle a été reportée (plusieurs reports permis depuis le 30/10). */
  reports: number
  /** Elle attend la mise à jour du BE (P&ID / PPL, EDE, HDS — utils/attenteBE). */
  attendBE: boolean
}

export interface MesActions {
  retard: MonAction[]
  /** À échéance d'ici vendredi (fin de la semaine de travail), pas en retard. */
  semaine: MonAction[]
  /** Plus tard, ou sans échéance. */
  plusTard: MonAction[]
}

/**
 * Le vendredi de la semaine en cours : une semaine de travail s'arrête là (Guillaume,
 * 28/10 : « chelou de parler de dimanche pour un outil de travail »). Le week-end,
 * « cette semaine » est la prochaine : son vendredi.
 */
export function finDeSemaine(aujourdhui: Jour): Jour {
  const rang = rangDansSemaine(aujourdhui) // lundi = 0 … dimanche = 6
  return decalerJours(aujourdhui, rang <= 4 ? 4 - rang : 11 - rang)
}

/** Mes actions ouvertes (FNC-T ouvertes ou en relevé seulement), rangées par urgence. */
export function mesActions(fncts: readonly Fnct[], actions: readonly ActionFnct[], qui: Qui, aujourdhui: Jour): MesActions {
  const parNumero = new Map(fncts.map((f) => [f.numero.toUpperCase(), f]))
  const parFnct = new Map<string, ActionFnct[]>()
  for (const a of actions) parFnct.set(a.fnct.toUpperCase(), [...(parFnct.get(a.fnct.toUpperCase()) ?? []), a])
  const finSemaine = finDeSemaine(aujourdhui)
  const res: MesActions = { retard: [], semaine: [], plusTard: [] }
  for (const a of actions) {
    const f = parNumero.get(a.fnct.toUpperCase())
    if (!f || f.cloture) continue
    if (!estMoi(a.responsable, a.responsableUPN, qui)) continue
    const statut = statutAction(a, aujourdhui)
    if (estTerminee(statut)) continue
    const echeance = echeanceEnVigueur(a)
    const m: MonAction = {
      action: a,
      fnct: f,
      echeance,
      retard: statut === 'en-retard' && echeance ? joursEntre(echeance, aujourdhui) : 0,
      reports: nombreDeReports(a),
      attendBE: !!beAttendu(a, f, parFnct.get(a.fnct.toUpperCase()) ?? []),
    }
    if (statut === 'en-retard') res.retard.push(m)
    else if (echeance && echeance <= finSemaine) res.semaine.push(m)
    else res.plusTard.push(m)
  }
  const parDate = (x: MonAction, y: MonAction): number => (x.echeance ?? '9999').localeCompare(y.echeance ?? '9999') || x.action.id.localeCompare(y.action.id)
  res.retard.sort(parDate)
  res.semaine.sort(parDate)
  res.plusTard.sort(parDate)
  return res
}

const pluriel = (n: number, mot: string): string => `${n} ${mot}${n > 1 ? 's' : ''}`

/** Le titre du bandeau « Pour vous ». */
export function titrePourVous(m: MesActions): string {
  const morceaux = [m.retard.length ? `${pluriel(m.retard.length, 'action')} en retard` : '', m.semaine.length ? `${m.semaine.length} cette semaine` : ''].filter(Boolean)
  if (morceaux.length) return morceaux.join(', ')
  return m.plusTard.length ? 'Rien en retard ni cette semaine' : 'Aucune action à votre nom'
}

/** « Aujourd’hui », « Demain », « Mercredi 21/10 », « Le 13/11 », « Sans échéance ». */
export function quandEcheance(echeance: Jour | null, aujourdhui: Jour): string {
  if (!echeance) return 'Sans échéance'
  const n = joursEntre(aujourdhui, echeance)
  if (n === 0) return 'Aujourd’hui'
  if (n === 1) return 'Demain'
  const jj = `${echeance.slice(8, 10)}/${echeance.slice(5, 7)}`
  if (n > 1 && n < 7) return `${JOURS[(rangDansSemaine(echeance) + 1) % 7]} ${jj}`
  return `Le ${jj}`
}
const JOURS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']

// ─── Les files de la page (24/10, même forme que la revue) ───

/** Les quatre tuiles de « Mes FNC-T ». */
export type FileMes = 'retard' | 'semaine' | 'plusTard' | 'closes'

/** La file montrée d'office : la plus pressée qui n'est pas vide, sinon les closes. */
export function fileParDefaut(m: MesActions): FileMes {
  if (m.retard.length) return 'retard'
  if (m.semaine.length) return 'semaine'
  if (m.plusTard.length) return 'plusTard'
  return 'closes'
}

export interface MaClose {
  action: ActionFnct
  fnct: Fnct
  /** Close avant l'échéance en vigueur (la reportée si elle existe). */
  aTemps: boolean
  /** Jours de retard à la clôture ; 0 si à temps ou sans échéance. */
  retard: number
}

/** Mes actions closes cette année, la plus récemment close d'abord. */
export function mesActionsCloses(fncts: readonly Fnct[], actions: readonly ActionFnct[], qui: Qui, aujourdhui: Jour): MaClose[] {
  const parNumero = new Map(fncts.map((f) => [f.numero.toUpperCase(), f]))
  const annee = aujourdhui.slice(0, 4)
  const res: MaClose[] = []
  for (const a of actions) {
    if (!a.cloture || a.cloture.slice(0, 4) !== annee || !estMoi(a.responsable, a.responsableUPN, qui)) continue
    const f = parNumero.get(a.fnct.toUpperCase())
    if (!f) continue
    const statut = statutAction(a, aujourdhui)
    const limite = echeanceEnVigueur(a)
    res.push({ action: a, fnct: f, aTemps: statut === 'terminee-a-temps', retard: statut === 'terminee-en-retard' && limite ? joursEntre(limite, a.cloture) : 0 })
  }
  return res.sort((x, y) => (y.action.cloture ?? '').localeCompare(x.action.cloture ?? '') || x.action.id.localeCompare(y.action.id))
}

// ─── À temps (24/10 : l'objectif est de clore à temps, autant le montrer) ───

export interface BilanATemps {
  /** Mes actions closes depuis le 1er janvier. */
  closes: number
  /** … dont closes à temps (avant l'échéance en vigueur, la reportée comprise). */
  aTemps: number
}

/** Mes actions closes cette année, et combien à temps (même règle que la base : statutAction). */
export function bilanATemps(actions: readonly ActionFnct[], qui: Qui, aujourdhui: Jour): BilanATemps {
  const annee = aujourdhui.slice(0, 4)
  let closes = 0
  let aTemps = 0
  for (const a of actions) {
    if (!a.cloture || a.cloture.slice(0, 4) !== annee || !estMoi(a.responsable, a.responsableUPN, qui)) continue
    closes += 1
    if (statutAction(a, aujourdhui) === 'terminee-a-temps') aTemps += 1
  }
  return { closes, aTemps }
}

/** « Cette année : 9 de vos 10 actions closes l’ont été à temps. » ; vide si rien n'est clos. */
export function texteBilan(b: BilanATemps, vous: boolean): string {
  if (b.closes === 0) return ''
  const ses = vous ? 'vos' : 'ses'
  if (b.closes === 1) return `Cette année : ${ses === 'vos' ? 'votre' : 'sa'} seule action close l’a été ${b.aTemps ? 'à temps' : 'en retard'}.`
  if (b.aTemps === b.closes) return `Cette année : ${ses} ${b.closes} actions closes l’ont toutes été à temps.`
  return `Cette année : ${b.aTemps} de ${ses} ${b.closes} actions closes l’ont été à temps.`
}

// ─── Mes FNC-T ───

/** `actions` (24/10) : j'y suis responsable d'au moins une action. */
export type RaisonFnct = 'ouverte' | 'mene' | 'actions' | 'suivie'
export type EtatMaFnct = 'retard' | 'prete' | 'encours' | 'releve' | 'close'

export interface MaFnct {
  fnct: Fnct
  actions: ActionFnct[]
  raisons: RaisonFnct[]
  etat: EtatMaFnct
  retards: number
  /** Mes actions encore ouvertes sur cette FNC-T. */
  miennes: number
}

/** Les closes restent un an dans « Terminées ». */
export const JOURS_TERMINEES = 365

const RANG_ETAT: Record<EtatMaFnct, number> = { retard: 0, prete: 1, encours: 2, releve: 3, close: 4 }

export function mesFnct(
  fncts: readonly Fnct[],
  actions: readonly ActionFnct[],
  qui: Qui,
  suivies: readonly string[],
  aujourdhui: Jour,
): MaFnct[] {
  const parFnct = new Map<string, ActionFnct[]>()
  for (const a of actions) {
    const k = a.fnct.toUpperCase()
    parFnct.set(k, [...(parFnct.get(k) ?? []), a])
  }
  const res: MaFnct[] = []
  for (const f of fncts) {
    const raisons: RaisonFnct[] = []
    if (estMoi(f.enregistrePar, undefined, qui)) raisons.push('ouverte')
    if (estMoi(f.leader, f.leaderUPN, qui)) raisons.push('mene')
    const siennes = parFnct.get(f.numero.toUpperCase()) ?? []
    const miennes = siennes.filter((a) => estMoi(a.responsable, a.responsableUPN, qui))
    if (miennes.length > 0) raisons.push('actions')
    if (suitFnct(suivies, f.numero)) raisons.push('suivie')
    if (raisons.length === 0) continue
    if (f.cloture && joursEntre(f.cloture, aujourdhui) > JOURS_TERMINEES) continue
    const retards = siennes.filter((a) => statutAction(a, aujourdhui) === 'en-retard').length
    const etat: EtatMaFnct = f.cloture ? 'close' : estEnReleve(f) ? 'releve' : retards > 0 ? 'retard' : pretAClore(f, siennes) ? 'prete' : 'encours'
    res.push({ fnct: f, actions: siennes, raisons, etat, retards, miennes: miennes.filter((a) => !a.cloture).length })
  }
  return res.sort((x, y) => {
    if (RANG_ETAT[x.etat] !== RANG_ETAT[y.etat]) return RANG_ETAT[x.etat] - RANG_ETAT[y.etat]
    // Les closes : la plus récemment close d'abord ; les autres : la plus récente d'abord.
    const cx = x.etat === 'close' ? x.fnct.cloture : debutFnct(x.fnct)
    const cy = y.etat === 'close' ? y.fnct.cloture : debutFnct(y.fnct)
    return (cy ?? '').localeCompare(cx ?? '') || y.fnct.numero.localeCompare(x.fnct.numero)
  })
}

export type FiltreMesFnct = 'toutes' | RaisonFnct

export function compterMesFnct(l: readonly MaFnct[]): Record<FiltreMesFnct, number> {
  return {
    toutes: l.length,
    ouverte: l.filter((m) => m.raisons.includes('ouverte')).length,
    mene: l.filter((m) => m.raisons.includes('mene')).length,
    actions: l.filter((m) => m.raisons.includes('actions')).length,
    suivie: l.filter((m) => m.raisons.includes('suivie')).length,
  }
}

/**
 * « ouverte par vous le 18/08 · vous la menez · 2 actions pour vous » ; « suivie »
 * seulement si c'est la seule raison. Pour une autre personne (« Voir pour ») :
 * « ouverte le 18/08 · leader · 2 actions à son nom ».
 */
export function raisonsLisibles(m: MaFnct, vous: boolean): string {
  const r: string[] = []
  if (m.raisons.includes('ouverte')) {
    const d = m.fnct.enregistrement
    r.push(`ouverte${vous ? ' par vous' : ''}${d ? ` le ${d.slice(8, 10)}/${d.slice(5, 7)}` : ''}`)
  }
  if (m.raisons.includes('mene')) r.push(vous ? 'vous la menez' : 'leader')
  if (m.raisons.includes('actions')) {
    if (m.miennes === 0) r.push(vous ? 'vos actions sont faites' : 'ses actions sont faites')
    else r.push(`${pluriel(m.miennes, 'action')} ${vous ? 'pour vous' : 'à son nom'}`)
  }
  if (m.raisons.includes('suivie') && r.length === 0) r.push('suivie')
  return r.join(' · ')
}

/** Les personnes proposées à « Voir pour » : responsables, leaders, enregistreurs, une fois chacun. */
export function personnesDesFnct(fncts: readonly Fnct[], actions: readonly ActionFnct[]): string[] {
  const vus = new Map<string, string>()
  const noter = (nom: string): void => {
    const k = cleNom(nom)
    if (nom.trim() && !vus.has(k)) vus.set(k, nom.trim())
  }
  for (const a of actions) noter(a.responsable)
  for (const f of fncts) {
    noter(f.leader)
    noter(f.enregistrePar)
  }
  return [...vus.values()].sort((a, b) => a.localeCompare(b, 'fr'))
}
